/**
 * 货币工具函数
 * 提供统一的货币格式化和处理能力
 */
import { SupportedCurrencies } from '../constants/system-config';
/**
 * 货币元数据映射
 */
export const CURRENCY_METADATA = {
    USDT: {
        code: 'USDT',
        symbol: '₮',
        name: '泰达币',
        decimals: 2,
        locale: 'en-US',
    },
    AED: {
        code: 'AED',
        symbol: 'د.إ',
        name: '阿联酋迪拉姆',
        decimals: 2,
        locale: 'ar-AE',
    },
    USD: {
        code: 'USD',
        symbol: '$',
        name: '美元',
        decimals: 2,
        locale: 'en-US',
    },
    CNY: {
        code: 'CNY',
        symbol: '¥',
        name: '人民币',
        decimals: 2,
        locale: 'zh-CN',
    },
    EUR: {
        code: 'EUR',
        symbol: '€',
        name: '欧元',
        decimals: 2,
        locale: 'de-DE',
    },
};
/**
 * 格式化货币金额
 * @param amount 金额（默认为最小单位，如分）
 * @param currency 货币代码
 * @param options 格式化选项
 * @returns 格式化后的字符串
 */
export function formatCurrency(amount, currency = 'USDT', options = {}) {
    const { showSymbol = true, useCode = false, decimals, fromCents = true, } = options;
    // 获取货币元数据，如果不存在则使用默认值
    const metadata = CURRENCY_METADATA[currency] ?? {
        code: currency,
        symbol: currency,
        name: currency,
        decimals: 2,
        locale: 'en-US',
    };
    // 转换为主单位（如分转元）
    const value = fromCents ? amount / 100 : amount;
    const decimalPlaces = decimals ?? metadata.decimals;
    // 格式化数字
    const formattedNumber = value.toLocaleString('zh-CN', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
    if (!showSymbol) {
        return formattedNumber;
    }
    if (useCode) {
        return `${metadata.code} ${formattedNumber}`;
    }
    return `${metadata.symbol}${formattedNumber}`;
}
/**
 * 解析货币金额字符串为分
 * @param value 金额字符串
 * @returns 分（最小单位）
 */
export function parseCurrencyToCents(value) {
    if (typeof value === 'number') {
        return Math.round(value * 100);
    }
    // 移除货币符号和空格
    const cleanValue = value.replace(/[^\d.-]/g, '');
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) {
        return 0;
    }
    return Math.round(numValue * 100);
}
/**
 * 获取货币选择器选项列表
 * @returns 用于 Select 组件的选项数组
 */
export function getCurrencyOptions() {
    return SupportedCurrencies.map((code) => ({
        value: code,
        label: `${code} - ${CURRENCY_METADATA[code].name}`,
    }));
}
/**
 * 获取货币元数据
 * @param currency 货币代码
 * @returns 货币元数据
 */
export function getCurrencyMetadata(currency) {
    return CURRENCY_METADATA[currency];
}
/**
 * 验证是否为有效的货币代码
 * @param currency 货币代码
 * @returns 是否有效
 */
export function isValidCurrency(currency) {
    return SupportedCurrencies.includes(currency);
}
//# sourceMappingURL=currency.js.map